import { useEffect } from 'react'
import { androidBilling, canPurchaseInApp } from '../utils/platform'
import './PremiumOfferModal.css'

interface PremiumOfferModalProps {
  isOpen: boolean
  onClose: () => void
  onPurchaseConfirmed: () => void
}

// Modal de oferta Premium do Capoeira Soul.
// Pagamento via Google Play Billing (R$ 49,90) — fluxo:
// 1. Botão chama window.AndroidBilling.buyPremium()
// 2. Kotlin abre fluxo nativo do Play e recebe purchaseToken
// 3. Kotlin chama window.onPremiumPurchaseToken(token)
// 4. React envia token para POST /api/premium → backend valida com Google
//    Developer API e marca is_premium=1 no DB
// 5. React esconde modal e remove AdsFooter

export default function PremiumOfferModal({
  isOpen,
  onClose,
  onPurchaseConfirmed,
}: PremiumOfferModalProps) {
  useEffect(() => {
    // Listener global pro callback do Kotlin
    if (typeof window === 'undefined') return
    ;(window as any).onPremiumPurchaseToken = async (token: string) => {
      try {
        const res = await fetch('/api/premium', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('cs_token') ?? ''}`,
          },
          body: JSON.stringify({
            gateway: 'google_play',
            gateway_ref: token,
            product_id: 'capoeira_soul_premium_lifetime',
            amount_cents: 4990,
          }),
        })
        const data = await res.json()
        if (data.ok) {
          // Notifica Kotlin pra reconhecer (consumir) a compra
          androidBilling()?.acknowledgePremium?.(token)
          onPurchaseConfirmed()
        } else {
          console.error('Premium register failed:', data.error)
        }
      } catch (e) {
        console.error('Erro ao registrar compra:', e)
      }
    }
  }, [onPurchaseConfirmed])

  if (!isOpen) return null

  const handlePurchase = () => {
    const billing = androidBilling()
    if (billing?.buyPremium) {
      billing.buyPremium()
    } else {
      alert(
        'Compra disponível apenas no aplicativo Android. Baixe o Capoeira Soul na Play Store.',
      )
    }
  }

  const purchaseAvailable = canPurchaseInApp()

  return (
    <div className="premium-modal-overlay" onClick={onClose}>
      <div className="premium-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          className="premium-modal-close"
          onClick={onClose}
          aria-label="Fechar"
        >
          ✕
        </button>

        <div className="premium-modal-header">
          <div className="premium-badge">PREMIUM</div>
          <h2>Torne-se um Mestre</h2>
          <p>Apoie o projeto e jogue sem interrupções</p>
        </div>

        <div className="premium-benefits">
          <div className="benefit-item">
            <span className="benefit-icon">🚫</span>
            <div className="benefit-text">
              <strong>Zero Anúncios</strong>
              <span>Remove o banner do rodapé para sempre.</span>
            </div>
          </div>

          <div className="benefit-item">
            <span className="benefit-icon">👑</span>
            <div className="benefit-text">
              <strong>Selo de Mestre Fundador</strong>
              <span>Distintivo dourado no seu perfil e no ranking.</span>
            </div>
          </div>

          <div className="benefit-item">
            <span className="benefit-icon">⚔️</span>
            <div className="benefit-text">
              <strong>Apoie a Capoeira</strong>
              <span>Ajude a manter o jogo vivo e a desenvolver novos capítulos.</span>
            </div>
          </div>
        </div>

        <div className="premium-footer">
          <div className="premium-price">
            <span className="price-label">Pagamento Único</span>
            <span className="price-value">R$ 49,90</span>
          </div>

          <button
            className="premium-buy-btn"
            onClick={handlePurchase}
            disabled={!purchaseAvailable && !isAndroidEnv()}
          >
            {purchaseAvailable ? 'QUERO SER MESTRE' : 'COMPRAR (APENAS NO APP)'}
          </button>

          <p className="premium-disclaimer">
            Compra vitalícia vinculada à sua conta Google. Sem assinaturas
            recorrentes.
          </p>
        </div>
      </div>
    </div>
  )
}

function isAndroidEnv(): boolean {
  return typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent)
}
